import axios, {type ResponseType} from 'axios';
import {useMessageHandler} from '@/helpers/error/useMessageHandler';
import {useAxiosStore} from "@/stores/axios-store";
import {useSignalRStore} from "@/stores/signalr/signalr-store";
import {useAuthStore} from "@/stores/authentication-store";
import {acquireTokenSilent} from "@/services/sso-service";

const ANTI_FORGERY_HEADER_NAME = 'X-XSRF-TOKEN';
const AUTH_CHANGED_HEADER_NAME = 'X-AUTH-CHANGED';
const ANTIFORGERY_TOKEN_ENDPOINT = 'authentication/antiForgeryToken';
const AUTH_ME_ENDPOINT = 'authentication/me';
/** Pause before the one retry that absorbs a lagging auth cookie (see the 401 handler). */
const AUTH_RETRY_DELAY_MS = 150;

const isUnsafeMethod = (requestMethod?: string) => {
  const method = (requestMethod ?? 'get').toLowerCase();
  return method === 'post' || method === 'put' || method === 'patch' || method === 'delete';
}

export const apiHttpClient = (handleError: boolean = true) => {
  let url = import.meta.env.VITE_API_API_URL as string;
  return createHttpClient(url, 'json', handleError);
};

const createHttpClient = (url: string, responseType: ResponseType, handleError: boolean = true) => {
  const httpClient = axios.create({
    baseURL: url,
    responseType: responseType,
    withCredentials: true,
    headers: {
      'Timezone': GetBrowserTimeZone(),
      'PortalConnectionId': useSignalRStore().GetConnectionId(),
    }
  })

  // Add abort signal to all requests
  httpClient.interceptors.request.use(async function (request) {
    if (!request.url?.includes(ANTIFORGERY_TOKEN_ENDPOINT) && !request.url?.includes(AUTH_ME_ENDPOINT)) {
      request.signal = useAxiosStore().abortController.signal;
    }
    request.withCredentials = true;
    
    if (isUnsafeMethod(request.method)) {
      await useAuthStore().ensureAntiForgeryTokenHydrated();
      request.headers[ANTI_FORGERY_HEADER_NAME] = useAuthStore().antiForgeryToken;
    }

    return request;
  })

  httpClient.interceptors.response.use(
    function (response) {
      const authChanged = (response.headers as any)?.[AUTH_CHANGED_HEADER_NAME.toLowerCase()];
      if (authChanged) {
        useAuthStore().antiForgeryTokenHydrated = false;
      }
      return response;
    },
    async function (error) {
      const status = error?.response?.status;
      const originalRequest = error?.config;

      // Auth-bootstrap endpoints must never trigger the 401 session-expired flow:
      // logout would recurse (it 401s when the session is already gone), and
      // /me + /antiForgeryToken legitimately 401 for unauthenticated visitors.
      const requestUrl = originalRequest?.url ?? '';
      const isAuthBootstrapEndpoint =
        requestUrl.includes('authentication/logout') ||
        requestUrl.includes(AUTH_ME_ENDPOINT) ||
        requestUrl.includes(ANTIFORGERY_TOKEN_ENDPOINT);

      if (status === 401 && isAuthBootstrapEndpoint) {
        return Promise.reject(error);
      }

      // Always retry once on 419, also for clients with handleError = false.
      if (
        originalRequest &&
        !originalRequest._antiForgeryRetried &&
        isUnsafeMethod(originalRequest.method) &&
        status === 419 // 419 Authentication Timeout is a non-standard status code commonly used to indicate an expired or invalid anti-forgery token.
      ) {
        originalRequest._antiForgeryRetried = true;

        // Force-refresh token and retry with new header.
        await useAuthStore().ensureAntiForgeryTokenHydrated(true);
        originalRequest.headers[ANTI_FORGERY_HEADER_NAME] = useAuthStore().antiForgeryToken;

        return httpClient.request(originalRequest);
      }

      // If the retry also failed with 419, the antiforgery cookie is unrecoverably gone.
      // Redirect to login so a fresh page load re-establishes it.
      if (
        originalRequest?._antiForgeryRetried &&
        status === 419
      ) {
        await useAuthStore().logout();
        return Promise.reject(error);
      }

      // Retry once on 401 for SSO users by silently acquiring a new token from MSAL.
      // Only attempt if the user was already authenticated — a 401 on page load (e.g. /me)
      // is expected and should not trigger a silent re-auth attempt.
      if (
        originalRequest &&
        !originalRequest._ssoRetried &&
        status === 401 &&
        useAuthStore().isAuthenticated()
      ) {
        originalRequest._ssoRetried = true;

        const result = await acquireTokenSilent();
        if (result) {
          try {
            const ok = await useAuthStore().ssoLogin(result.idToken);
            if (ok)
              return httpClient.request(originalRequest);
          } catch {
            // SSO token refresh failed (e.g. SSO_MULTIPLE_ROLES_ASSIGNED).
            // Return the original 401 directly — do NOT fall through to handleError,
            // which would call handleSessionExpired and trigger a navigation loop.
            return Promise.reject(error);
          }
        }
      }

      // The auth cookie may have just been replaced (login, SSO, switchOrganization).
      // WebKit does not commit a Set-Cookie from an XHR response in step with that
      // request resolving, so a 401 right afterwards can be that race rather than a
      // dead session. Retry once after a short pause; a second 401 means it is really
      // gone. handleSessionExpired clears the store, so isAuthenticated() is false
      // from then on and expired sessions cannot loop through here.
      if (
        originalRequest &&
        !originalRequest._authRetried &&
        status === 401 &&
        useAuthStore().isAuthenticated()
      ) {
        originalRequest._authRetried = true;
        await new Promise(resolve => setTimeout(resolve, AUTH_RETRY_DELAY_MS));
        return httpClient.request(originalRequest);
      }

      if (handleError && error.response) {
        const errorHandler = useMessageHandler();
        await errorHandler.handleError(error.response);
      }
      return Promise.reject(error);
    }
  );

  return httpClient
}

export const GetBrowserTimeZone = () => {
  //Javascript GetTimezoneOffset always shows the opposite
  return Math.abs(new Date().getTimezoneOffset())
}
